// Copyright (c) Ping. All rights reserved.
//
// Tiny shared helper that resolves (or lazily mints) the MLS
// `Conversation` proxy that backs a BE conversationId, mirroring the
// inline `resolveConv` in `WebChatRoom.tsx`. Extracted so non-composer
// hosts (e.g. `ChatRouteHost`'s ShareLocation / Poll / Game overlays)
// can `conv.send` an envelope without duplicating the
// `getOrCreateMlsConversation` plumbing.
//
// This intentionally does NOT cache across calls — the WebChatRoom keeps
// its own `convProxyRef` cache for the hot text/voice send path. The
// overlay hosts call this at most once per user action, so a fresh
// resolve is fine.

import { log } from '@core/log/log';
import { getCurrentTransport } from '@session/sessionBootstrap';

import { getOrCreateMlsConversation } from './mlsConversation';

// Mirror of the SDK's MessageEnvelope return from `conv.send`. Only the
// fields we use are typed.
export interface SentMessageEnvelopeLite {
  conversation_id: Uint8Array;
  epoch: number | bigint;
  content_hash: Uint8Array;
  hlc: { wall_ms: number | bigint; logical: number };
}

export interface SendableConv {
  send(plaintext: Uint8Array): Promise<SentMessageEnvelopeLite>;
}

export async function resolveActiveConv(params: {
  readonly client: unknown | null;
  readonly conversationId: string;
  readonly conversationName?: string | undefined;
  /**
   * Best-effort callers (e.g. the DeviceGroup `conv_state` sibling sync) pass
   * `true` so a failed/stale MLS resolve logs at DEBUG instead of ERROR/WARN. Such
   * callers already treat a miss as non-fatal — the local Dexie write is
   * authoritative and siblings reconcile on their next sync — so the loud
   * `cache_stale` / `list_failed` / `invite.no_local_row` lines are pure noise for
   * them (they matter only on the interactive chat-send path, which omits `quiet`).
   */
  readonly quiet?: boolean;
}): Promise<SendableConv | null> {
  const { client, conversationId, conversationName, quiet } = params;
  if (client === null) return null;
  const transport = getCurrentTransport();
  // Route every level to DEBUG when quiet, so a best-effort resolve failure never
  // surfaces as an error/warn in the console.
  const resolveLog = quiet === true
    ? {
        debug: (meta: Record<string, unknown>, msg?: string) => log.debug(meta, msg),
        info: (meta: Record<string, unknown>, msg?: string) => log.debug(meta, msg),
        warn: (meta: Record<string, unknown>, msg?: string) => log.debug(meta, msg),
        error: (meta: Record<string, unknown>, msg?: string) => log.debug(meta, msg),
      }
    : log;
  const result = await getOrCreateMlsConversation({
    client: client as Parameters<typeof getOrCreateMlsConversation>[0]['client'],
    transport,
    beConversationId: conversationId,
    // `conversationName` is a required key on the options type (its
    // VALUE may be undefined), so pass it through unconditionally
    // rather than via a conditional spread.
    conversationName,
    log: resolveLog,
  });
  return result.conv as unknown as SendableConv;
}
